/**
 * Shared Constants for Caribbean Data Protection Compliance
 * Single source of truth — matches backend configurations.
 *
 * To add a new jurisdiction/country:
 *   1. Add to JURISDICTIONS below (drives DSR form, Readiness, filters, widgets, labels everywhere)
 *   2. Add to COUNTRIES if it needs to appear in country dropdowns (Vendor, Client, Entity)
 *   4. Add to backend/models/dsr.py  JURISDICTION_CONFIG  (DSR deadline calculation)
 *   5. Add to frontend/src/pages/compliance/breach/constants.js  JURISDICTION_BREACH_RULES
 */

// ---------------------------------------------------------------------------
// Caribbean Jurisdictions — drives DSR form, filters, ByJurisdiction widget,
// jurisdiction label display across the whole app.
// ---------------------------------------------------------------------------
export const JURISDICTIONS = [
  // Laws in force
  { value: 'JM', label: 'Jamaica',                      flag: '🇯🇲', act: 'Data Protection Act, 2020',                 deadlineDays: 30 },
  { value: 'BB', label: 'Barbados',                     flag: '🇧🇧', act: 'Data Protection Act, 2019',                 deadlineDays: 30 },
  { value: 'TT', label: 'Trinidad and Tobago',          flag: '🇹🇹', act: 'Data Protection Chap. 22:04',               deadlineDays: 30 },
  { value: 'KY', label: 'Cayman Islands',               flag: '🇰🇾', act: 'Data Protection Act, 2017',                 deadlineDays: 30 },
  { value: 'BM', label: 'Bermuda',                      flag: '🇧🇲', act: 'Personal Information Protection Act, 2016', deadlineDays: 45 },
  { value: 'BZ', label: 'Belize',                       flag: '🇧🇿', act: 'Data Protection Act',                       deadlineDays: 30 },
  // Pending enactment
  { value: 'BS', label: 'Bahamas',                      flag: '🇧🇸', act: 'Data Protection Bill, 2025',                deadlineDays: 30, pending_enactment: true },
  { value: 'GD', label: 'Grenada',                      flag: '🇬🇩', act: 'Data Protection Act (expected Aug 2025)',   deadlineDays: 30, pending_enactment: true },
  // No law yet — available for readiness assessments and client/entity registration
  { value: 'AG', label: 'Antigua & Barbuda',            flag: '🇦🇬', act: null, pending_enactment: true },
  { value: 'AI', label: 'Anguilla',                     flag: '🇦🇮', act: null, pending_enactment: true },
  { value: 'AW', label: 'Aruba',                        flag: '🇦🇼', act: null, pending_enactment: true },
  { value: 'VG', label: 'British Virgin Islands',       flag: '🇻🇬', act: null, pending_enactment: true },
  { value: 'CW', label: 'Curaçao',                      flag: '🇨🇼', act: null, pending_enactment: true },
  { value: 'DM', label: 'Dominica',                     flag: '🇩🇲', act: null, pending_enactment: true },
  { value: 'GY', label: 'Guyana',                       flag: '🇬🇾', act: null, pending_enactment: true },
  { value: 'MS', label: 'Montserrat',                   flag: '🇲🇸', act: null, pending_enactment: true },
  { value: 'SX', label: 'Sint Maarten',                 flag: '🇸🇽', act: null, pending_enactment: true },
  { value: 'KN', label: 'St. Kitts & Nevis',            flag: '🇰🇳', act: null, pending_enactment: true },
  { value: 'LC', label: 'St. Lucia',                    flag: '🇱🇨', act: null, pending_enactment: true },
  { value: 'VC', label: 'St. Vincent & the Grenadines', flag: '🇻🇨', act: null, pending_enactment: true },
  { value: 'SR', label: 'Suriname',                     flag: '🇸🇷', act: null, pending_enactment: true },
  { value: 'TC', label: 'Turks & Caicos Islands',       flag: '🇹🇨', act: null, pending_enactment: true },
];

// Lookup helpers — use these instead of inline maps throughout the app
export const getJurisdictionByCode = (code) =>
  JURISDICTIONS.find(j => j.value === code) || null;

export const getJurisdictionLabel = (code) => {
  const j = getJurisdictionByCode(code);
  return j ? `${j.flag} ${j.label}` : (code || 'Unknown');
};

export const getJurisdictionAct = (code) =>
  getJurisdictionByCode(code)?.act || '';

// Select-ready array: [{ value, label }] with flag in label — for <Select> dropdowns
export const JURISDICTION_OPTIONS = JURISDICTIONS.map(j => ({
  value: j.value,
  label: `${j.label} ${j.flag}`,
}));

// ---------------------------------------------------------------------------
// Countries — used in Vendor, Client, Entity dropdowns.
// Includes all Caribbean jurisdictions plus common international locations.
// adequacy: true = Caribbean/EU/EEA adequacy determination
// ---------------------------------------------------------------------------
export const COUNTRIES = [
  // Caribbean — all 22 client-specified countries
  { code: 'JM', name: 'Jamaica',                        adequacy: true  },
  { code: 'BB', name: 'Barbados',                        adequacy: true  },
  { code: 'TT', name: 'Trinidad and Tobago',             adequacy: true  },
  { code: 'KY', name: 'Cayman Islands',                  adequacy: true  },
  { code: 'BM', name: 'Bermuda',                         adequacy: true  },
  { code: 'BZ', name: 'Belize',                          adequacy: true  },
  { code: 'BS', name: 'Bahamas',                         adequacy: true  },
  { code: 'GD', name: 'Grenada',                         adequacy: false },
  { code: 'AG', name: 'Antigua & Barbuda',               adequacy: false },
  { code: 'AI', name: 'Anguilla',                        adequacy: false },
  { code: 'AW', name: 'Aruba',                           adequacy: false },
  { code: 'VG', name: 'British Virgin Islands',          adequacy: false },
  { code: 'CW', name: 'Curaçao',                         adequacy: false },
  { code: 'DM', name: 'Dominica',                        adequacy: false },
  { code: 'GY', name: 'Guyana',                          adequacy: false },
  { code: 'MS', name: 'Montserrat',                      adequacy: false },
  { code: 'SX', name: 'Sint Maarten',                    adequacy: false },
  { code: 'KN', name: 'St. Kitts & Nevis',               adequacy: false },
  { code: 'LC', name: 'St. Lucia',                       adequacy: false },
  { code: 'VC', name: 'St. Vincent & the Grenadines',    adequacy: false },
  { code: 'SR', name: 'Suriname',                        adequacy: false },
  { code: 'TC', name: 'Turks & Caicos Islands',          adequacy: false },
  // International — commented out; ROPA transfer risk uses its own country list (ROPAFormPage.jsx TRANSFER_RISK_CONFIG)
  // { code: 'US', name: 'United States',                   adequacy: false },
  // { code: 'CA', name: 'Canada',                          adequacy: true  },
  // { code: 'GB', name: 'United Kingdom',                  adequacy: true  },
  // { code: 'IE', name: 'Ireland',                         adequacy: true  },
  // { code: 'DE', name: 'Germany',                         adequacy: true  },
  // { code: 'NL', name: 'Netherlands',                     adequacy: true  },
  // { code: 'IN', name: 'India',                           adequacy: false },
  // { code: 'SG', name: 'Singapore',                       adequacy: true  },
  // { code: 'AU', name: 'Australia',                       adequacy: true  },
  // { code: 'PH', name: 'Philippines',                     adequacy: false },
  // { code: 'OTHER', name: 'Other',                        adequacy: false },
];

// Simple name list for dropdowns that just need strings (EntityManagement, ClientManagement)
export const COUNTRY_NAMES = COUNTRIES.map(c => c.name);

// Caribbean adequacy countries (for ROPA transfer risk — no-warning list)
export const CARIBBEAN_ADEQUACY_COUNTRIES = COUNTRIES
  .filter(c => c.adequacy && ['JM','BB','TT','BS','KY','BM','BZ'].includes(c.code))
  .map(c => c.name);

export const getCountryName = (code) =>
  COUNTRIES.find(c => c.code === code)?.name || code;

export const isAdequateCountry = (code) =>
  COUNTRIES.find(c => c.code === code)?.adequacy ?? false;

// ---------------------------------------------------------------------------
// Risk Tiers
// ---------------------------------------------------------------------------
export const RISK_TIERS = {
  tier_1: { id: 'tier_1', name: 'Tier 1 - Critical', threshold: 75, reviewDays: 90,  color: 'text-red-600'   },
  tier_2: { id: 'tier_2', name: 'Tier 2 - High',     threshold: 50, reviewDays: 180, color: 'text-orange-600' },
  tier_3: { id: 'tier_3', name: 'Tier 3 - Medium',   threshold: 25, reviewDays: 365, color: 'text-amber-600'  },
  tier_4: { id: 'tier_4', name: 'Tier 4 - Low',      threshold: 0,  reviewDays: 730, color: 'text-green-600'  },
};

export const getRiskTierInfo  = (tier) => RISK_TIERS[tier] || null;
export const getRiskTierColor = (tier) => RISK_TIERS[tier]?.color || '';
export const getRiskTierName  = (tier) => RISK_TIERS[tier]?.name || tier;

// ---------------------------------------------------------------------------
// Data Categories (GDPR/Caribbean aligned)
// ---------------------------------------------------------------------------
export const DATA_CATEGORIES = [
  { id: 'contact_info',      name: 'Contact Information',  sensitivity: 'low'     },
  { id: 'identification',    name: 'Identification Data',  sensitivity: 'medium'  },
  { id: 'financial',         name: 'Financial Data',       sensitivity: 'high'    },
  { id: 'employment',        name: 'Employment Data',      sensitivity: 'medium'  },
  { id: 'health',            name: 'Health Data',          sensitivity: 'special' },
  { id: 'biometric',         name: 'Biometric Data',       sensitivity: 'special' },
  { id: 'location',          name: 'Location Data',        sensitivity: 'medium'  },
  { id: 'online_identifiers',name: 'Online Identifiers',   sensitivity: 'low'     },
  { id: 'children_data',     name: "Children's Data",      sensitivity: 'special' },
];

// ---------------------------------------------------------------------------
// Data Subject Categories
// ---------------------------------------------------------------------------
export const DATA_SUBJECTS = [
  { id: 'employees',   name: 'Employees'          },
  { id: 'customers',   name: 'Customers'          },
  { id: 'prospects',   name: 'Prospects/Leads'    },
  { id: 'suppliers',   name: 'Suppliers/Vendors'  },
  { id: 'contractors', name: 'Contractors'        },
  { id: 'applicants',  name: 'Job Applicants'     },
  { id: 'members',     name: 'Members'            },
  { id: 'visitors',    name: 'Website Visitors'   },
  { id: 'other',       name: 'Other'              },
];

// ---------------------------------------------------------------------------
// Legal Bases for Processing
// ---------------------------------------------------------------------------
export const LEGAL_BASES = [
  { id: 'contract',             name: 'Contract Performance'  },
  { id: 'consent',              name: 'Consent'               },
  { id: 'legal_obligation',     name: 'Legal Obligation'      },
  { id: 'vital_interests',      name: 'Vital Interests'       },
  { id: 'public_task',          name: 'Public Task'           },
  { id: 'legitimate_interests', name: 'Legitimate Interests'  },
];

// ---------------------------------------------------------------------------
// Service Categories for Vendors
// ---------------------------------------------------------------------------
export const SERVICE_CATEGORIES = [
  { id: 'it_infrastructure', name: 'IT Infrastructure'  },
  { id: 'software_saas',     name: 'Software/SaaS'      },
  { id: 'hr_services',       name: 'HR Services'        },
  { id: 'marketing',         name: 'Marketing'          },
  { id: 'finance',           name: 'Finance'            },
  { id: 'legal',             name: 'Legal'              },
  { id: 'security',          name: 'Security'           },
  { id: 'customer_service',  name: 'Customer Service'   },
  { id: 'logistics',         name: 'Logistics'          },
  { id: 'consulting',        name: 'Consulting'         },
  { id: 'other',             name: 'Other'              },
];

// ---------------------------------------------------------------------------
// Vendor Lifecycle Stages
// ---------------------------------------------------------------------------
export const LIFECYCLE_STAGES = [
  { id: 'prospect',      name: 'Prospect'       },
  { id: 'due_diligence', name: 'Due Diligence'  },
  { id: 'contracting',   name: 'Contracting'    },
  { id: 'active',        name: 'Active'         },
  { id: 'under_review',  name: 'Under Review'   },
  { id: 'remediation',   name: 'Remediation'    },
  { id: 'suspended',     name: 'Suspended'      },
  { id: 'offboarding',   name: 'Offboarding'    },
  { id: 'terminated',    name: 'Terminated'     },
];

// ---------------------------------------------------------------------------
// DSR Request Types
// ---------------------------------------------------------------------------
export const DSR_REQUEST_TYPES = [
  { id: 'access',               name: 'Access Request',       description: 'Get a copy of personal data'          },
  { id: 'rectification',        name: 'Rectification',        description: 'Correct personal data'                },
  { id: 'erasure',              name: 'Erasure',              description: 'Delete personal data'                 },
  { id: 'blocking',             name: 'Blocking',             description: 'Block processing (Jamaica/Cayman)'   },
  { id: 'restriction',          name: 'Restriction',          description: 'Restrict processing'                  },
  { id: 'portability',          name: 'Portability',          description: 'Transfer data'                        },
  { id: 'objection',            name: 'Objection',            description: 'Object to processing'                 },
  { id: 'prevent_processing',   name: 'Prevent Processing',   description: 'Stop harmful use (Jamaica)'          },
  { id: 'objection_marketing',  name: 'Marketing Opt-Out',    description: 'Stop marketing'                      },
  { id: 'automated_decision',   name: 'Automated Decision',   description: 'Human review'                        },
  { id: 'withdraw_consent',     name: 'Withdraw Consent',     description: 'Withdraw consent'                    },
  { id: 'complaint',            name: 'Complaint',            description: 'Data protection complaint'           },
  { id: 'other',                name: 'Other',                description: 'Other request'                       },
];

// ---------------------------------------------------------------------------
// DSR Statuses
// ---------------------------------------------------------------------------
export const DSR_STATUSES = {
  received:      { id: 'received',      name: 'Received',          state: 'open'   },
  verifying:     { id: 'verifying',     name: 'Verifying Identity', state: 'open'   },
  awaiting_info: { id: 'awaiting_info', name: 'Awaiting Info',      state: 'open'   },
  processing:    { id: 'processing',    name: 'Processing',         state: 'open'   },
  reviewing:     { id: 'reviewing',     name: 'Under Review',       state: 'open'   },
  responded:     { id: 'responded',     name: 'Responded',          state: 'closed' },
  closed:        { id: 'closed',        name: 'Closed',             state: 'closed' },
  withdrawn:     { id: 'withdrawn',     name: 'Withdrawn',          state: 'closed' },
  refused:       { id: 'refused',       name: 'Refused',            state: 'closed' },
};

// ---------------------------------------------------------------------------
// Breach Statuses
// ---------------------------------------------------------------------------
export const BREACH_STATUSES = {
  reported:      { id: 'reported',      name: 'Reported',      state: 'open'   },
  investigating: { id: 'investigating', name: 'Investigating',  state: 'open'   },
  contained:     { id: 'contained',     name: 'Contained',      state: 'open'   },
  notifying:     { id: 'notifying',     name: 'Notifying',      state: 'open'   },
  remediation:   { id: 'remediation',   name: 'Remediation',    state: 'open'   },
  closed:        { id: 'closed',        name: 'Closed',         state: 'closed' },
  not_a_breach:  { id: 'not_a_breach',  name: 'Not a Breach',   state: 'closed' },
};
